<template>
  <div class="language-switcher">
    <div class="lang-dropdown" @click="toggleDropdown" v-click-outside="closeDropdown">
      <div class="lang-trigger">
        <svg class="globe-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
        <span class="current-lang">{{ currentLanguage.toUpperCase() }}</span>
        <svg class="chevron-icon" :class="{ 'rotated': isDropdownOpen }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </div>
      
      <div class="lang-options" v-show="isDropdownOpen" :class="{ 'show': isDropdownOpen }">
        <div 
          class="lang-option" 
          :class="{ 'active': currentLanguage === 'en' }"
          @click="switchLanguage('en')"
        >
          <span class="flag-emoji">🇺🇸</span>
          <span>English</span>
        </div>
        <div 
          class="lang-option" 
          :class="{ 'active': currentLanguage === 'es' }"
          @click="switchLanguage('es')"
        >
          <span class="flag-emoji">🇪🇸</span>
          <span>Español</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LanguageSwitcher',
  data() {
    return {
      isDropdownOpen: false
    }
  },
  computed: {
    currentLanguage() {
      return this.$i18n.locale
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    closeDropdown() {
      this.isDropdownOpen = false
    },
    switchLanguage(language) {
      if (language === this.currentLanguage) {
        this.closeDropdown()
        return
      }
      
      this.$i18n.locale = language
      
      // Save to localStorage
      localStorage.setItem('language', language)
      
      // Update document language
      document.documentElement.lang = language
      
      // Close dropdown with slight delay for visual feedback
      setTimeout(() => {
        this.isDropdownOpen = false
      }, 150)
    }
  },
  mounted() {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'es')) {
      this.$i18n.locale = savedLanguage
      document.documentElement.lang = savedLanguage
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  }
}
</script>

<style scoped>
.language-switcher {
  position: relative;
  z-index: 9999;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lang-dropdown {
  position: relative;
  cursor: pointer;
  user-select: none;
  height: 100%;
  display: flex;
  align-items: center;
}

.lang-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #fff;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.lang-trigger::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.05), transparent);
  transition: left 0.5s ease;
}

.lang-trigger:hover::before {
  left: 100%;
}

.lang-trigger:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.globe-icon {
  width: 18px;
  height: 18px;
  color: #017c9b;
  opacity: 0.95;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.current-lang {
  font-size: 13px;
  font-weight: 700;
  color: #017c9b;
  letter-spacing: 0.8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.chevron-icon {
  width: 14px;
  height: 14px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #017c9b;
  opacity: 0.9;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.lang-options {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 160px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 10000;
}

.lang-options.show {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #374151;
  position: relative;
  font-weight: 500;
}

.lang-option:hover {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  color: #0369a1;
  transform: translateX(4px);
}

.lang-option.active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  font-weight: 600;
}

.lang-option.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.flag-emoji {
  font-size: 18px;
  width: 24px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.lang-option span:last-child {
  font-size: 14px;
  font-weight: 500;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .lang-trigger {
    padding: 6px 12px;
    gap: 6px;
    min-width: 65px;
  }
  
  .globe-icon {
    width: 16px;
    height: 16px;
  }
  
  .current-lang {
    font-size: 12px;
  }
  
  .chevron-icon {
    width: 12px;
    height: 12px;
  }
  
  .lang-options {
    min-width: 140px;
    right: -10px;
  }
  
  .lang-option {
    padding: 12px 16px;
  }
  
  .flag-emoji {
    font-size: 16px;
    width: 20px;
    height: 16px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .lang-trigger {
    padding: 5px 10px;
    gap: 5px;
    min-width: 55px;
  }
  
  .globe-icon {
    width: 14px;
    height: 14px;
  }
  
  .current-lang {
    font-size: 11px;
  }
  
  .chevron-icon {
    width: 10px;
    height: 10px;
  }
  
  .lang-options {
    min-width: 120px;
    right: -5px;
  }
  
  .lang-option {
    padding: 10px 14px;
  }
  
  .flag-emoji {
    font-size: 14px;
    width: 18px;
    height: 14px;
  }
}

/* Tablet responsive */
@media (max-width: 1024px) {
  .lang-trigger {
    padding: 7px 14px;
    gap: 7px;
    min-width: 75px;
  }
  
  .globe-icon {
    width: 17px;
    height: 17px;
  }
  
  .current-lang {
    font-size: 12px;
  }
  
  .chevron-icon {
    width: 13px;
    height: 13px;
  }
}
</style>